import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import { AuthContext } from "../../../../../context/authContext";
import { getPlantData, getProjectData } from "../PMSTab2/4d/api";
import { getUserTasks } from "../../../../../api/tasksApi";
import { getPlantMis, upsertPlantMis } from "../../../../../api/misApi";
import { secondAxios } from "../../../../../api/axios";
import { getMcsStatusForTasks } from "../MCS3/Schedule4D/api";
import {
  getScheduleTasks,
  enrichTasksWithMcsStatus,
} from "../MCS3/Schedule4D/scheduleTaskApi";
import { getTotalCost as getMaterialCost } from "../MCS3/Schedule4D/columns";
import { useSchedule4D } from "../MCS3/Schedule4D/Schedule4DContext";
import { getTaskCodeLevel } from "../../../Project/plant/PlantTaskPage/TaskList/components/taskCodeUtils";
import { normalizeTaskType } from "../../../Project/plant/PlantTaskPage/TaskList/components/constants";

const MISContext = createContext({
  receivablesRows: [],
  setReceivablesRows: () => {},
  hoursRows: [],
  invoicesRows: [],
  setInvoicesRows: () => {},
  manhoursRows: [],
  setManhoursRows: () => {},
  displayMonths: ["jan", "feb", "mar"],
  loading: false,
  poPayablesData: [],
  poPayablesLoading: false,
  procurementCalculatedProgress: null,
  totalAvgProgress: 0,
  setTotalAvgProgress: () => {},
});

const monthKeys = [
  "jan",
  "feb",
  "mar",
  "apr",
  "may",
  "jun",
  "jul",
  "aug",
  "sep",
  "oct",
  "nov",
  "dec",
];

const getMonthKey = (dateValue) => {
  if (!dateValue) return null;
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return null;
  return monthKeys[date.getMonth()];
};

const getTaskCode = (task) =>
  task?.taskCode || task?.task_code || task?.code || task?.task_id;

const MIS_DISCIPLINE_TYPES = ["engineering", "procurement", "fabrication"];

/** Same task scope as Plant TaskList TaskStats (per discipline tab). */
const filterMisTasksLikeTaskList = (tasks) =>
  (Array.isArray(tasks) ? tasks : []).filter((task) => {
    if (task?.isArchived) return false;
    const taskCode = String(getTaskCode(task) ?? "").trim();
    if (!taskCode) return false;
    const type = normalizeTaskType(task?.type);
    if (!MIS_DISCIPLINE_TYPES.includes(type)) return false;
    return getTaskCodeLevel(taskCode) >= 2;
  });

const getMisDisciplineLabel = (task) => {
  const type = normalizeTaskType(task?.type);
  if (!MIS_DISCIPLINE_TYPES.includes(type)) return null;
  return type.charAt(0).toUpperCase() + type.slice(1);
};

// Prefer grouping by task "type" (e.g. subcategory name) for MIS receivables
const getTaskLabel = (task) =>
  // From userTasks shape (see staff/MyTasks*)
  task?.subCategory?.name ||
  // Possible alternate shapes
  task?.taskType ||
  task?.type ||
  task?.name ||
  // Fallbacks
  getTaskCode(task);

const getTaskDate = (task) =>
  task?.startDate || task?.actualStart || task?.targetDate || task?.endDate;

/** Normalize description for matching (same as Schedule4D) */
const normalizeDescription = (str) => {
  if (str == null || str === "") return "";
  return String(str)
    .trim()
    .replace(/\s+/g, " ")
    .replace(/\s*,\s*/g, ", ");
};

/** Calculate progress from MRQ/PO/MRV status (30/60/100) */
const calculateProcurementProgressFromMcs = (row) => {
  const hasMrq = row.mrqMaterials?.some((m) => m?.mrq?.id);
  const hasPo = row.mrqMaterials?.some(
    (m) => m?.poId || m?.poMaterials?.[0]?.po?.id,
  );
  const hasMrv =
    row.mrvMaterials?.[0]?.id ||
    row.mrqMaterials?.some((m) => m?.mrvMrqMaterials?.[0]?.mrv?.id);
  if (hasMrv) return 100;
  if (hasPo) return 60;
  if (hasMrq) return 30;
  return 0;
};

/** Build map of taskCode/task -> progress for MIS receivables lookup */
const buildProcurementProgressMapForMIS = (taskRows) => {
  const map = new Map();
  (taskRows || []).forEach((row) => {
    const progress = calculateProcurementProgressFromMcs(row);
    const taskCode = normalizeDescription(row.taskCode || "");
    const taskDesc =
      normalizeDescription(row.task ?? row.description ?? "") ||
      (row.taggedMaterial
        ? `${row.taggedMaterial?.component ?? ""} ${row.taggedMaterial?.size ?? ""}`.trim()
        : "");
    if (taskCode && taskCode.toLowerCase() !== "procurement") {
      map.set(taskCode, progress);
    }
    if (taskDesc && taskDesc !== "-") {
      map.set(taskDesc, progress);
    }
  });
  const values = Array.from(map.values()).filter(
    (v) => v != null && !Number.isNaN(Number(v)),
  );
  const avgProgress =
    values.length > 0
      ? values.reduce((a, b) => a + Number(b), 0) / values.length
      : null;
  return { map, avgProgress };
};

// For hours aggregation, prefer grouping by task "type" when it is
// one of the main MIS disciplines (Engineering, Fabrication),
// otherwise fall back to the generic task label.
const getHoursLabel = (task) => {
  const type = task?.type;
  const lowerType = type?.toLowerCase?.();
  if (lowerType === "engineering" || lowerType === "fabrication") {
    return type;
  }
  return getTaskLabel(task);
};

const createEmptyReceivablesRow = (label) => ({
  id: label,
  task: label,
  firstDate: "",
  averageProgress: 0,
  taskCount: 0,
  jan: 0,
  feb: 0,
  mar: 0,
  apr: 0,
  may: 0,
  jun: 0,
  jul: 0,
  aug: 0,
  sep: 0,
  oct: 0,
  nov: 0,
  dec: 0,
});

/** Receivables progress — same formula as Plant TaskList TaskStats: avg(task.progress) per discipline. */
const buildMonthlyRows = (tasks) => {
  const rowsByLabel = new Map();
  const progressTotalsByLabel = new Map();
  const monthStatsByLabel = new Map();
  const disciplineOrder = ["Engineering", "Procurement", "Fabrication"];

  (Array.isArray(tasks) ? tasks : []).forEach((task) => {
    const label = getMisDisciplineLabel(task);
    if (!label) return;

    const prog = parseFloat(task.progress);
    if (Number.isNaN(prog)) return;

    if (!rowsByLabel.has(label)) {
      rowsByLabel.set(label, createEmptyReceivablesRow(label));
      progressTotalsByLabel.set(label, { total: 0, count: 0 });
      monthStatsByLabel.set(label, {});
    }

    const totals = progressTotalsByLabel.get(label);
    totals.total += prog;
    totals.count += 1;

    const monthKey = getMonthKey(getTaskDate(task));
    if (monthKey) {
      const monthStats = monthStatsByLabel.get(label);
      if (!monthStats[monthKey]) {
        monthStats[monthKey] = { sum: 0, count: 0 };
      }
      monthStats[monthKey].sum += prog;
      monthStats[monthKey].count += 1;
    }

    const row = rowsByLabel.get(label);
    const taskDate = getTaskDate(task);
    if (taskDate) {
      if (!row.firstDate) {
        row.firstDate = taskDate;
      } else {
        const rowDate = new Date(row.firstDate);
        const d = new Date(taskDate);
        if (!Number.isNaN(d.getTime()) && d < rowDate) {
          row.firstDate = taskDate;
        }
      }
    }
  });

  disciplineOrder.forEach((label) => {
    if (!rowsByLabel.has(label)) return;

    const row = rowsByLabel.get(label);
    const totals = progressTotalsByLabel.get(label);
    row.taskCount = totals.count;
    row.averageProgress = totals.count ? totals.total / totals.count : 0;

    const monthStats = monthStatsByLabel.get(label) || {};
    monthKeys.forEach((key) => {
      const stat = monthStats[key];
      row[key] = stat?.count ? stat.sum / stat.count : 0;
    });
  });

  return disciplineOrder
    .filter((label) => rowsByLabel.has(label))
    .map((label) => rowsByLabel.get(label));
};

// Build hours rows from tasks using actualHours field
const buildHoursRows = (tasks) => {
  // Group by task type/label (Engineering, Fabrication) and sum actualHours by month
  const rowsByLabel = new Map();
  (Array.isArray(tasks) ? tasks : []).forEach((task) => {
    const label = getHoursLabel(task);
    if (!label) return;
    const monthKey = getMonthKey(getTaskDate(task));
    const hours = Number(
      task?.actualHours ?? task?.hours ?? task?.manhours ?? 0,
    );

    if (!rowsByLabel.has(label)) {
      rowsByLabel.set(label, {
        id: label,
        task: label,
        jan: 0,
        feb: 0,
        mar: 0,
        apr: 0,
        may: 0,
        jun: 0,
        jul: 0,
        aug: 0,
        sep: 0,
        oct: 0,
        nov: 0,
        dec: 0,
      });
    }

    const row = rowsByLabel.get(label);
    if (monthKey) {
      row[monthKey] = (Number(row[monthKey]) || 0) + hours;
    }
  });

  return Array.from(rowsByLabel.values());
};

const buildManhoursRows = (monthlyRows) =>
  monthlyRows.map((row, index) => ({
    id: `${row.task}-${index}`,
    task: row.task,
    date: row.firstDate || "",
    manhours: monthKeys.reduce((sum, key) => sum + (Number(row[key]) || 0), 0),
    piping: "",
    structural: "",
    equipment: "",
    welding: "",
    fabrication: "",
  }));
// Build manpower loading rows: one row per Engineering/Fabrication task
const buildManpowerRowsFromTasks = (tasks) => {
  const normalizeDate = (value) => {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    return d.toISOString().slice(0, 10); // YYYY-MM-DD
  };

  return (Array.isArray(tasks) ? tasks : [])
    .filter((task) => {
      const type = task?.type?.toLowerCase?.();
      return type === "engineering" || type === "fabrication";
    })
    .map((task, index) => {
      const type = task.type.toLowerCase();
      const discipline = type.charAt(0).toUpperCase() + type.slice(1); // Engineering / Fabrication

      const startDateValue =
        task.startDate ||
        task.actualStart ||
        task.targetDate ||
        task.endDate ||
        task.createdAt;

      const endDateValue = task.endDate || task.targetDate || startDateValue;

      const description =
        task.task ||
        task.name ||
        task.subCategory?.name ||
        task.description ||
        getTaskLabel(task);

      const budget =
        Number(
          task.estimatedTime ??
            task.estimatedHours ??
            task.actualHours ??
            task.hours ??
            task.manhours ??
            0,
        ) || 0;

      return {
        id: task.id || getTaskCode(task) || `${discipline}-${index}`,
        discipline,
        baselineStart: normalizeDate(startDateValue),
        baselineFinish: normalizeDate(endDateValue),
        description: description || "",
        budgetHours: budget,
      };
    });
};

const getDisplayMonths = (rows) => {
  let latestIndex = -1;
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    monthKeys.forEach((key, index) => {
      if (Number(row[key]) > 0) {
        latestIndex = Math.max(latestIndex, index);
      }
    });
  });
  if (latestIndex === -1) return ["jan", "feb", "mar"];
  return [
    monthKeys[latestIndex],
    monthKeys[(latestIndex + 1) % monthKeys.length],
    monthKeys[(latestIndex + 2) % monthKeys.length],
  ];
};

// Dummy data adjusted so each task averages to 80% progress
// This 80 will also appear as progress in the Invoices tab
const buildDemoReceivablesRows = () => [
  {
    id: 1,
    task: "Engineering",
    averageProgress: 80,
    taskCount: 1,
    jan: 75,
    feb: 78,
    mar: 82,
    apr: 80,
    may: 85,
    jun: 80,
  },
  {
    id: 2,
    task: "Procurement",
    averageProgress: 80,
    taskCount: 1,
    jan: 70,
    feb: 75,
    mar: 85,
    apr: 82,
    may: 88,
    jun: 80,
  },
  {
    id: 3,
    task: "Fabrication",
    averageProgress: 80,
    taskCount: 1,
    jan: 72,
    feb: 76,
    mar: 84,
    apr: 78,
    may: 90,
    jun: 80,
  },
];

const buildDemoManhoursRows = () => [
  {
    id: 1,
    task: "task-1",
    date: "2026-01-15",
    designersPiping: "12",
    designersStructural: "10",
    designersEquipment: "14",
    engineersPiping: "15",
    engineersStructural: "12",
    engineersEquipment: "18",
    weld: "20",
    fab: "19",
  },
  {
    id: 2,
    task: "task-2",
    date: "2026-02-10",
    designersPiping: "8",
    designersStructural: "6",
    designersEquipment: "10",
    engineersPiping: "12",
    engineersStructural: "9",
    engineersEquipment: "14",
    weld: "10",
    fab: "11",
  },
  {
    id: 3,
    task: "task-3",
    date: "2026-03-05",
    designersPiping: "14",
    designersStructural: "12",
    designersEquipment: "16",
    engineersPiping: "18",
    engineersStructural: "16",
    engineersEquipment: "20",
    weld: "22",
    fab: "24",
  },
];

// default structure for invoices when MIS has no data yet
// default fixed rows for invoices when MIS has no data yet
// Progress set to 80 to match dummy receivables data
const buildDemoInvoicesRows = () => [
  {
    id: 1,
    task: "Engineering",
    progress: 80,
    weightedCost: "",
  },
  {
    id: 2,
    task: "Procurement",
    progress: 80,
    weightedCost: "",
  },
  {
    id: 3,
    task: "Fabrication",
    progress: 80,
    weightedCost: "",
  },
];

const buildDemoDeviationRows = () => [
  {
    id: "1",
    category: "DCS",
    referenceSource: "Ref-001 / Source A",
    budgeted: 120,
    actual: 150,
    remarks: "Extra scope",
    actionTaken: "Approve variation",
    deviationStatus: "Open",
    responsiblePerson: "Ahmed",
  },
  {
    id: "2",
    category: "MCS",
    referenceSource: "Ref-010 / Source B",
    budgeted: 300,
    actual: 260,
    remarks: "Optimization",
    actionTaken: "Document savings",
    deviationStatus: "In Progress",
    responsiblePerson: "Khaled",
  },
  {
    id: "3",
    category: "PCS",
    referenceSource: "Ref-020 / Source C",
    budgeted: 90,
    actual: 90,
    remarks: "",
    actionTaken: "",
    deviationStatus: "Closed",
    responsiblePerson: "Omar",
  },
];

export const useMIS = () => useContext(MISContext);

export const MISProvider = ({ children }) => {
  const { plantId, projectId } = useParams();
  const { accessToken } = useContext(AuthContext);
  const schedule4D = useSchedule4D();
  const [loading, setLoading] = useState(false);
  const [receivablesRows, setReceivablesRows] = useState([]);
  const [totalAvgProgress, setTotalAvgProgress] = useState(0);
  const [hoursRows, setHoursRows] = useState([]);
  const [invoicesRows, setInvoicesRows] = useState([]);
  const [manhoursRows, setManhoursRows] = useState([]);
  const [displayMonths, setDisplayMonths] = useState(["jan", "feb", "mar"]);
  const [poPayablesData, setPoPayablesData] = useState([]);
  const [poPayablesLoading, setPoPayablesLoading] = useState(false);
  const [procurementCalculatedProgress, setProcurementCalculatedProgress] =
    useState(null);

  //////// deviatiion table states /////////

  const [deviationRows, setDeviationRows] = useState([]);
  const [deviationLoading, setDeviationLoading] = useState(false);
  const [deviationSaving, setDeviationSaving] = useState(false);

  const mapTypeToCategory = (type) => {
    const t = String(type || "").toLowerCase();
    if (t === "engineering") return "DCS";
    if (t === "fabrication") return "PCS";
    return "MCS";
  };

  const fetchDeviationRows = async (tasks, options = {}) => {
    const { scheduleMaterials = [], schedulePlantId, plantId, misData } =
      options;
    const taskRows = tasks
      .map((task, idx) => {
        const category = mapTypeToCategory(task?.type);
        if (!category) return null;

        return {
          id: `task-${idx}`,
          category,
          referenceSource: task?.taskCode,
          actual: task?.manhours,
          budgeted: task?.estimatedTime,
          remarks: "",
          actionTaken: "",
          deviationStatus: "",
          responsiblePerson: task.task_members?.[0] ?? "",
        };
      })
      .filter(Boolean);

    let rows = [...taskRows];

    if (
      Array.isArray(scheduleMaterials) &&
      scheduleMaterials.length > 0 &&
      schedulePlantId &&
      plantId &&
      schedulePlantId === plantId
    ) {
      const mcsRows = scheduleMaterials.map((material, idx) => {
        const description =
          material.description ||
          (material.taggedMaterial
            ? `${material.taggedMaterial?.component ?? ""} ${material.taggedMaterial?.size ?? ""}`.trim()
            : "") ||
          "Material";
        const budgeted =
          material.budgetedCost != null && material.budgetedCost !== ""
            ? Number(material.budgetedCost)
            : 0;
        const actual = getMaterialCost(material);
        const actualNum =
          actual != null && !Number.isNaN(Number(actual)) ? Number(actual) : 0;
        return {
          id: `mcs-${idx}`,
          category: "MCS",
          referenceSource: description,
          budgeted,
          actual: actualNum,
          remarks: "",
          actionTaken: "",
          deviationStatus: "",
          responsiblePerson: "",
        };
      });
      rows = [...rows, ...mcsRows];
    }

    // Merge saved editable fields from MIS JSON (same access pattern as invoices)
    const savedDeviations = misData?.data?.data?.deviations || {};
    rows = rows.map((row) => {
      const saved = savedDeviations[row.id];
      if (saved) {
        return {
          ...row,
          remarks: saved.remarks ?? row.remarks ?? "",
          actionTaken: saved.actionTaken ?? row.actionTaken ?? "",
          deviationStatus: saved.deviationStatus ?? row.deviationStatus ?? "",
          responsiblePerson:
            saved.responsiblePerson ?? row.responsiblePerson ?? "",
        };
      }
      return row;
    });

    setDeviationRows(rows);
  };

  const saveDeviationsToApi = async (rows) => {
    if (!plantId) return;
    try {
      const deviationsPayload = {};
      rows.forEach((row) => {
        deviationsPayload[row.id] = {
          remarks: row.remarks ?? "",
          actionTaken: row.actionTaken ?? "",
          deviationStatus: row.deviationStatus ?? "",
          responsiblePerson: row.responsiblePerson ?? "",
        };
      });
      console.log("deviationsPayload", deviationsPayload);
      // Load existing MIS data so we don't overwrite invoices or other keys
      let existing = {};
      try {
        const currentMis = await getPlantMis(plantId);
        existing = currentMis?.data?.data || {};
      } catch {
        // No existing data yet
      }

      await upsertPlantMis(plantId, {
        ...existing,
        deviations: deviationsPayload,
      });
    } catch (e) { 
    
      console.error("Failed to save deviation data:", e);
    }
  };

  const updateDeviationRow = async (rowId, patch) => {
    setDeviationSaving(true);
    try {
      const updatedRows = deviationRows.map((r) =>
        r.id === rowId ? { ...r, ...patch } : r,
      );
      setDeviationRows(updatedRows);
      await saveDeviationsToApi(updatedRows);
    } catch (e) {
      console.error("Failed to update deviation row:", e);
    } finally {
      setDeviationSaving(false);
    }
  };

  // helper to map MIS JSON shape <-> local state
  // MIS JSON structure:
  // {
  //   invoices: {
  //     engineering: { progress, weightedCost, inv1: { date, amount }, inv2: {...}, ... },
  //     procurement: { ... },
  //     fabrication: { ... }
  //   }
  // }
  const buildInvoicesFromMis = (misData) => {
    const base = buildDemoInvoicesRows();

    const invoicesJson = misData?.data?.data?.invoices || {};

    return base.map((row, idx) => {
      const key = row.task.toLowerCase();
      const storedTask = invoicesJson[key] || {};

      const storedProgress =
        storedTask.progress !== undefined && storedTask.progress !== null
          ? Number(storedTask.progress)
          : undefined;

      const result = {
        id: idx + 1,
        task: row.task,
        // If MIS has a stored progress, use it; otherwise fall back to demo default (80)
        progress:
          storedProgress !== undefined ? storedProgress : (row.progress ?? 80),
        // Keep any stored weighted cost for backward compatibility, though UI will derive it
        weightedCost: storedTask.weightedCost ?? row.weightedCost ?? "",
      };

      // Flatten nested invN objects into invNDate / invNAmount fields on the row
      Object.entries(storedTask).forEach(([k, v]) => {
        const match = k.match(/^inv(\d+)$/);
        if (!match) return;
        const n = match[1];
        const inv = v || {};
        result[`inv${n}Date`] = inv.date ?? "";
        result[`inv${n}Amount`] = inv.amount ?? "";
      });

      return result;
    });
  };

  useEffect(() => {
    const load = async () => {
      // if (!accessToken) return;
      setLoading(true);
      setDeviationLoading(true);
      try {
        // 1) Load MIS JSON for invoices (and potentially other MIS data later)
        let misData = null;
        if (plantId) {
          misData = await getPlantMis(plantId);
        }

        // Prefer tasks from tasks API for MIS receivables
        let tasks = [];
        let procurementProgressMap = new Map();
        let avgProcurementProgress = null;

        let scheduleMaterialsForDeviation = [];
        if (plantId) {
          const useCachedSchedule =
            schedule4D?.schedulePlantId === plantId &&
            Array.isArray(schedule4D?.scheduleMaterials);
          scheduleMaterialsForDeviation = useCachedSchedule
            ? schedule4D.scheduleMaterials
            : [];
          tasks = await getPlantData(plantId, accessToken);
          if (!useCachedSchedule) {
            const [scheduleTasks, mcsStatus] = await Promise.all([
              getScheduleTasks({ plantId, accessToken }).catch(() => []),
              getMcsStatusForTasks(plantId).catch(() => ({
                byTaskCode: new Map(),
                byTaskId: new Map(),
              })),
            ]);
            scheduleMaterialsForDeviation = enrichTasksWithMcsStatus(
              scheduleTasks ?? [],
              mcsStatus,
            );
          }
          const { map: progMap } = buildProcurementProgressMapForMIS(
            scheduleMaterialsForDeviation,
          );
          procurementProgressMap = progMap;
          const progValues = Array.from(procurementProgressMap.values()).filter(
            (v) => v != null && !Number.isNaN(Number(v)),
          );
          avgProcurementProgress =
            progValues.length > 0
              ? progValues.reduce((a, b) => a + Number(b), 0) /
                progValues.length
              : null;
          setProcurementCalculatedProgress(avgProcurementProgress);
        } else if (projectId) {
          tasks = await getProjectData(projectId, accessToken);
        }

        tasks = filterMisTasksLikeTaskList(tasks);

        if (plantId) {
          await fetchDeviationRows(tasks, {
            scheduleMaterials: scheduleMaterialsForDeviation,
            schedulePlantId: schedule4D?.schedulePlantId ?? plantId,
            plantId,
            misData,
          });
        }

        const monthlyRows = buildMonthlyRows(tasks);
        const taskHoursRows = buildHoursRows(tasks);
        const manpowerRows = buildManpowerRowsFromTasks(tasks);

        // Use tasks-based monthly rows for receivables
        setReceivablesRows(
          monthlyRows.length > 0 ? monthlyRows : buildDemoReceivablesRows(),
        );

        // Use tasks-based hours rows (actualHours by month)
        setHoursRows(taskHoursRows);

        // Invoices: fixed rows, progress from MIS JSON if available; override Procurement with calculated (MRQ/PO/MRV)
        let invoiceRows = buildInvoicesFromMis(misData);
        if (avgProcurementProgress != null) {
          invoiceRows = invoiceRows.map((row) =>
            row.task?.toLowerCase() === "procurement"
              ? { ...row, progress: avgProcurementProgress }
              : row,
          );
        }
        setInvoicesRows(invoiceRows);
        // Manhours (Manpower Loading): one row per Engineering/Fabrication task
        setManhoursRows(
          manpowerRows.length > 0 ? manpowerRows : buildDemoManhoursRows(),
        );
        setDisplayMonths(
          monthlyRows.length > 0
            ? getDisplayMonths(monthlyRows)
            : ["jan", "feb", "mar"],
        );
      } catch (error) {
        console.error("Failed to load MIS data:", error);
        setReceivablesRows(buildDemoReceivablesRows());
        setHoursRows([]);
        setInvoicesRows(buildDemoInvoicesRows());
        setManhoursRows(buildDemoManhoursRows());
        setDisplayMonths(["jan", "feb", "mar"]);
        setDeviationRows([]);
        setProcurementCalculatedProgress(null);
        setTotalAvgProgress(0);
      } finally {
        setLoading(false);
        setDeviationLoading(false);
      }
    };
    load();
  }, [accessToken, plantId, projectId]);

  useEffect(() => {
    const rowAverages = receivablesRows
      .filter((row) => (row.taskCount ?? 0) > 0)
      .map((row) => Number(row.averageProgress) || 0);

    const totalCumulative = rowAverages.length
      ? rowAverages.reduce((sum, v) => sum + v, 0) / rowAverages.length
      : 0;

    setTotalAvgProgress(totalCumulative.toFixed(2));
  }, [receivablesRows]);

  // Fetch payments data for payables (per-PO payments)
  useEffect(() => {
    const fetchPlantPayments = async () => {
      if (!plantId) return;
      setPoPayablesLoading(true);
      try {
        // Get all payments for this plant; backend joins Payment -> Po
        const { data } = await secondAxios.get(`/payment/plant/${plantId}`);

        console.log("=== Fetched Payments for Payables (MISContext) ===");
        console.log("Payments:", data);

        const paymentsArray = Array.isArray(data) ? data : data ? [data] : [];

        const payablesRows = paymentsArray.map((payment, index) => {
          const po = payment.po || {};
          return {
            id: payment.id || `payment-${index}`,
            paymentDate: payment.paymentDate ?? payment.payment_date ?? "",
            amount: payment.amount ?? payment.paymentAmount ?? "",
            forWhom: payment.forWhom || "",
            approver: payment.approver || "",
            poNumber: po.poNumber || "",
            poId: po.id || payment.poId || "",
            remarks: payment.remarks || "",
          };
        });

        console.log("=== Final Payables Rows (from payments) ===");
        console.log(payablesRows);
        console.log("====================================");

        setPoPayablesData(payablesRows);
      } catch (error) {
        console.error("Error fetching payments for payables:", error);
        setPoPayablesData([]);
      } finally {
        setPoPayablesLoading(false);
      }
    };
    console.log("fetchPlantPayments");
    fetchPlantPayments();
  }, [plantId]);

  const value = useMemo(
    () => ({
      receivablesRows,
      setReceivablesRows,
      hoursRows,
      invoicesRows,
      setInvoicesRows,
      manhoursRows,
      setManhoursRows,
      displayMonths,
      loading,
      poPayablesData,
      poPayablesLoading,
      procurementCalculatedProgress,
      deviationRows,
      setDeviationRows,
      deviationLoading,
      deviationSaving,
      updateDeviationRow,
      saveDeviationsToApi,
      totalAvgProgress,
      setTotalAvgProgress,
    }),
    [
      receivablesRows,
      hoursRows,
      invoicesRows,
      manhoursRows,
      displayMonths,
      loading,
      poPayablesData,
      poPayablesLoading,
      procurementCalculatedProgress,
      deviationRows,
      deviationLoading,
      deviationSaving,
      totalAvgProgress,
    ],
  );

  return <MISContext.Provider value={value}>{children}</MISContext.Provider>;
};
